import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LineChart from '../../../../shared/components/chart/line-chart/LineChart';
import {
  DailyGroupPrompts,
  GroupStats,
} from '../../../../shared/models/agency-stats/AgencyStatsResponse';
import { OTHERS_COLOR } from '../../../../shared/utils/utils';

const FALLBACK_COLOR = '#6366f1';

interface AgencyStatsLineChartProps {
  dailyGroupPrompts?: DailyGroupPrompts[];
  groups: GroupStats[];
}

const AgencyStatsLineChart: React.FC<AgencyStatsLineChartProps> = ({ dailyGroupPrompts, groups }) => {
  const { t } = useTranslation();
  const ungroupedLabel = t('ungrouped');

  const { matrixData, colorPalette } = useMemo(() => {
    if (!dailyGroupPrompts || dailyGroupPrompts.length === 0) {
      return { matrixData: [['Date']] as (string | number)[][], colorPalette: [] as string[] };
    }

    const nameOf = (groupId: number | null, groupName: string): string =>
      groupId === null ? ungroupedLabel : groupName;

    const groupNames = new Set<string>();
    for (const day of dailyGroupPrompts) {
      for (const g of day.groups) {
        groupNames.add(nameOf(g.groupId, g.groupName));
      }
    }

    const sortedGroupNames = Array.from(groupNames).sort();
    const header: (string | number)[] = ['Date', ...sortedGroupNames];

    const colorMap = new Map<string, string>();
    for (const g of groups) {
      const name = nameOf(g.groupId, g.groupName);
      colorMap.set(name, g.groupId === null ? OTHERS_COLOR : g.groupColor ?? FALLBACK_COLOR);
    }

    const colors = sortedGroupNames.map((name) => colorMap.get(name) ?? FALLBACK_COLOR);

    const rows = dailyGroupPrompts.map((day) => {
      const countByGroup = new Map<string, number>();
      for (const g of day.groups) {
        countByGroup.set(nameOf(g.groupId, g.groupName), g.count);
      }
      const row: (string | number)[] = [day.date];
      for (const name of sortedGroupNames) {
        row.push(countByGroup.get(name) ?? 0);
      }
      return row;
    });

    return { matrixData: [header, ...rows], colorPalette: colors };
  }, [dailyGroupPrompts, groups, ungroupedLabel]);

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">{t('agencyStatsPromptsOverTime')}</div>
      </div>
      <div className="stats-box stats-box--chart">
        <div className="stats-box__content">
          <LineChart
            firstData={matrixData}
            firstColorPalette={colorPalette}
            isMax30={false}
          />
        </div>
      </div>
    </div>
  );
};

export default AgencyStatsLineChart;
